import React, { useCallback, useEffect, useState } from 'react'
import { learningService } from '../../services/learning'
import type {
  CoachingSession,
  GuardianLearner,
  GuardianProgressSummaryResponse,
  GuardianShare,
  LearningEvidence,
  LearningHome,
  RecommendationAiStructuredInsights,
  SkillRecommendation,
} from '../../types/learning'
import { LearningOnboarding } from './learning/LearningOnboarding'
import { FrenchPractice } from './learning/FrenchPractice'
import { AiCoachPanel } from './learning/AiCoachPanel'
import { JobSkillFocus } from './learning/JobSkillFocus'
import { AssignedLearning } from './learning/AssignedLearning'
import { useI18n } from '../../i18n/I18nContext'

const priorityClass = (priority: string) =>
  priority === 'high' ? 'status-pill status-pill--pending'
    : priority === 'medium' ? 'status-pill status-pill--draft'
      : 'status-pill status-pill--muted'

const asTextList = (value: unknown): string[] => {
  if (!value) return []
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
  return String(value).split(/\n|,/).map((item) => item.trim()).filter(Boolean)
}

const recommendationInsights = (recommendation: SkillRecommendation) => {
  const insights = recommendation.evidenceSummary?.aiStructuredInsights as RecommendationAiStructuredInsights | undefined
  const weaknesses = asTextList(insights?.weaknesses).slice(0, 3)
  const mistakes = asTextList(insights?.mistakes).slice(0, 3)
  const failedQuestions = asTextList(insights?.failedQuestions).slice(0, 3)
  const practicePlan = asTextList(insights?.practicePlan).slice(0, 3)
  if (!weaknesses.length && !mistakes.length && !failedQuestions.length && !practicePlan.length) return null
  return { weaknesses, mistakes, failedQuestions, practicePlan }
}

const RecommendationCard: React.FC<{ recommendation: SkillRecommendation }> = ({ recommendation }) => {
  const { t } = useI18n()
  const insights = recommendationInsights(recommendation)
  return (
    <div className="card stack" data-testid="learning-recommendation">
      <div className="button-row" style={{ justifyContent: 'space-between' }}>
        <strong>{recommendation.skillName || recommendation.skillTrack || t('Recommended skill')}</strong>
        <span className={priorityClass(recommendation.priority)}>{t(recommendation.priority)}</span>
      </div>
      <p className="text-muted">{recommendation.reason}</p>
      <p><em>{recommendation.suggestedNextAction}</em></p>
      {insights && (
        <div className="stack-sm" data-testid="learning-recommendation-insights">
          <strong>{t('Practice signals')}</strong>
          {insights.weaknesses.length ? <p className="helper">{t('Focus')}: {insights.weaknesses.join(', ')}</p> : null}
          {insights.mistakes.length ? <p className="helper">{t('Recurring mistakes')}: {insights.mistakes.join(', ')}</p> : null}
          {insights.failedQuestions.length ? <p className="helper">{t('Missed questions')}: {insights.failedQuestions.join(', ')}</p> : null}
          {insights.practicePlan.length ? (
            <ul>
              {insights.practicePlan.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </div>
      )}
      {recommendation.estimatedEffort && <p className="helper">{t('Estimated effort')}: {recommendation.estimatedEffort}</p>}
    </div>
  )
}

const scoreLabel = (score: number | null, maxScore: number | null) =>
  score != null && maxScore != null ? `${score}/${maxScore}` : null

const invitationLabel = (status: string | undefined) => {
  if (status === 'accepted') return 'Accepted'
  if (status === 'account_linked') return 'Account linked'
  if (status === 'revoked') return 'Revoked'
  return 'Invited'
}

const inviteDeliveryLabel = (status: string | undefined) => {
  if (status === 'in_app_sent') return 'Notice sent'
  if (status === 'email_sent') return 'Email sent'
  if (status === 'email_not_configured') return 'Email not configured'
  if (status === 'email_failed') return 'Email failed'
  if (status === 'awaiting_account') return 'Waiting for account'
  if (status === 'no_in_app_channel') return 'No in-app notice'
  if (status === 'revoked') return 'Notice closed'
  return 'Notice pending'
}

const inviteAttemptSummary = (
  share: GuardianShare,
  translate: (text: string, vars?: Record<string, string | number>) => string,
  dateLabel: (value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => string,
) => {
  const attempt = share.inviteDeliveryAttempts?.[0]
  if (!attempt) return null
  const channel = attempt.channel === 'in_app' ? translate('in-app') : attempt.channel === 'email' ? translate('email') : translate('none')
  const when = attempt.sentAt || attempt.createdAt
  return translate('{channel} attempt: {status}{date}', {
    channel,
    status: translate(inviteDeliveryLabel(attempt.status)),
    date: when ? ` · ${dateLabel(when, { month: 'short', day: 'numeric' })}` : '',
  })
}

const apiErrorMessage = (error: unknown, fallback: string) => {
  const message = (error as any)?.response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

// Platform Learning workspace — the learner home. Available to every authenticated
// user (including those with no organization). Shows onboarding first when the
// learner profile is not yet set up.
const LearningWorkspaceContent: React.FC<{coachRevision?:number}> = ({coachRevision=0}) => {
  const { t, formatDate } = useI18n()
  const [home, setHome] = useState<LearningHome | null>(null)
  const [coaching, setCoaching] = useState<CoachingSession[]>([])
  const [evidence, setEvidence] = useState<LearningEvidence[]>([])
  const [guardianShares, setGuardianShares] = useState<GuardianShare[]>([])
  const [guardianLearners, setGuardianLearners] = useState<GuardianLearner[]>([])
  const [guardianSummary, setGuardianSummary] = useState<GuardianProgressSummaryResponse | null>(null)
  const [guardianEmail, setGuardianEmail] = useState('')
  const [guardianRelationship, setGuardianRelationship] = useState('')
  const [guardianBusy, setGuardianBusy] = useState(false)
  const [guardianError, setGuardianError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (options: { quiet?: boolean } = {}) => {
    if (!options.quiet) setLoading(true)
    setError(null)
    setGuardianError(null)
    try {
      const [homeResponse, coachingResponse, evidenceResponse, sharesResponse, guardianLearnersResponse] = await Promise.all([
        learningService.getHome(),
        learningService.listCoaching(),
        learningService.listEvidence(),
        learningService.listGuardianShares(),
        learningService.listGuardianLearners(),
      ])
      setHome(homeResponse.data.home)
      setCoaching(coachingResponse.data.coaching || [])
      setEvidence(evidenceResponse.data.evidence || [])
      setGuardianShares(sharesResponse.data.shares || [])
      setGuardianLearners(guardianLearnersResponse.data.learners || [])
    } catch (err) {
      if (!options.quiet) setError(t('We could not load your learning home. Please retry.'))
    } finally {
      if (!options.quiet) setLoading(false)
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  // After a successful practice submission, regenerate recommendations without
  // remounting the active practice/assignment runner and hiding its feedback.
  const refreshAfterPractice = useCallback(() => {
    void learningService.generateRecommendations().catch(() => {
      // Non-fatal: persisted evidence is still visible on the next home load.
    })
  }, [])

  const submitGuardianShare = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!guardianEmail.trim()) return
    setGuardianBusy(true)
    setGuardianError(null)
    try {
      const response = await learningService.createGuardianShare({
        guardianEmail: guardianEmail.trim(),
        relationshipLabel: guardianRelationship.trim() || null,
      })
      setGuardianShares((shares) => [response.data.share, ...shares.filter((share) => share.id !== response.data.share.id)])
      setGuardianEmail('')
      setGuardianRelationship('')
    } catch (err) {
      setGuardianError(t('We could not create that guardian share. Check the email and try again.'))
    } finally {
      setGuardianBusy(false)
    }
  }

  const revokeGuardianShare = async (shareId: string) => {
    setGuardianBusy(true)
    setGuardianError(null)
    try {
      const response = await learningService.revokeGuardianShare(shareId)
      setGuardianShares((shares) => shares.map((share) => share.id === shareId ? response.data.share : share))
    } catch (err) {
      setGuardianError(t('We could not revoke that guardian share. Please retry.'))
    } finally {
      setGuardianBusy(false)
    }
  }

  const resendGuardianShare = async (shareId: string) => {
    setGuardianBusy(true)
    setGuardianError(null)
    try {
      const response = await learningService.resendGuardianShare(shareId)
      setGuardianShares((shares) => shares.map((share) => share.id === shareId ? { ...share, ...response.data.share } : share))
    } catch (err) {
      setGuardianError(apiErrorMessage(err, t('We could not resend that guardian invite. Please retry.')))
    } finally {
      setGuardianBusy(false)
    }
  }

  const openGuardianSummary = async (learnerUserId: string) => {
    setGuardianBusy(true)
    setGuardianError(null)
    try {
      const response = await learningService.getGuardianProgressSummary(learnerUserId)
      setGuardianSummary(response.data)
    } catch (err) {
      setGuardianError(t('We could not load that shared progress summary. The share may have been revoked.'))
    } finally {
      setGuardianBusy(false)
    }
  }

  const acceptGuardianShare = async (learnerUserId: string) => {
    setGuardianBusy(true)
    setGuardianError(null)
    try {
      const response = await learningService.acceptGuardianShare(learnerUserId)
      setGuardianLearners((learners) => learners.map((learner) => learner.learnerUserId === learnerUserId ? response.data.share : learner))
    } catch (err) {
      setGuardianError(t('We could not accept that shared learner invitation. Please retry.'))
    } finally {
      setGuardianBusy(false)
    }
  }

  if (loading) {
    return <section className="panel workspace-panel stack" data-testid="learning-workspace-loading"><p className="text-muted">{t('Loading your learning home…')}</p></section>
  }

  if (error || !home) {
    return (
      <section className="panel workspace-panel stack" data-testid="learning-workspace-error">
        <p className="text-muted">{error || t('No learning data available yet.')}</p>
        <div className="button-row"><button type="button" className="btn btn-secondary" onClick={() => void load()}>{t('Retry')}</button></div>
      </section>
    )
  }

  if (home.needsOnboarding) {
    return (
      <>
      {new URLSearchParams(window.location.search).has('assignmentId')?<AssignedLearning onAttemptSubmitted={refreshAfterPractice}/>:null}
      <LearningOnboarding
        starterTracks={home.starterTracks}
        defaultLanguage={home.profile.learningLanguage}
        onComplete={() => void load()}
      />
      </>
    )
  }

  const { profile, streak, continueLearning, recommendations, recommendedNextSkill, starterTracks, upcomingCoaching, dailyPractice, recentActivities } = home

  return (
    <section className="learning-workspace stack-lg" data-testid="learning-workspace">
      <header className="panel workspace-panel stack">
        <div className="button-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="stack">
            <h2 className="heading-lg">{profile.displayName ? t('Welcome back, {name}', { name: profile.displayName }) : t('Your learning home')}</h2>
            <p className="text-muted">{t('Keep building skills that belong to you — across every organization you ever join.')}</p>
          </div>
          <div className="admin-metric-card">
            <strong>{streak.currentStreakDays}🔥</strong>
            <span className="text-muted">{t('day streak')} · {profile.totalXp} XP</span>
          </div>
        </div>
      </header>

      <AssignedLearning onAttemptSubmitted={refreshAfterPractice} />

      <div className="stack">
        <h3>{t('Family Progress Sharing')}</h3>
        {guardianError && <div className="admin-error-banner" role="alert" data-testid="learning-guardian-error">{guardianError}</div>}
        <div className="card-grid">
          <form className="card stack" data-testid="learning-guardian-share-form" onSubmit={submitGuardianShare}>
            <label>
              {t('Guardian email')}
              <input className="input" type="email" value={guardianEmail} onChange={(event) => setGuardianEmail(event.target.value)} />
            </label>
            <label>
              {t('Relationship')}
              <input className="input" value={guardianRelationship} onChange={(event) => setGuardianRelationship(event.target.value)} placeholder={t('Parent, guardian, tutor')} />
            </label>
            <div className="button-row">
              <button type="submit" className="btn btn-primary" disabled={guardianBusy || !guardianEmail.trim()}>{t('Share progress')}</button>
            </div>
          </form>
          <div className="card stack" data-testid="learning-guardian-shares">
            <strong>{t('Shared with')}</strong>
            {guardianShares.length === 0
              ? <p className="text-muted">{t('No guardian shares yet.')}</p>
              : guardianShares.slice(0, 6).map((share) => {
                const attemptSummary = inviteAttemptSummary(share, t, formatDate)
                return (
                  <div key={share.id} className="admin-list-row">
                    <div>
                      <strong>{share.guardianName || share.guardianEmail}</strong>
                      <p className="helper">{share.relationshipLabel || t('Guardian')} · {t(invitationLabel(share.invitationStatus || share.status))} · {t(inviteDeliveryLabel(share.inviteDeliveryStatus))}</p>
                      {attemptSummary && <p className="helper">{attemptSummary}</p>}
                    </div>
                    {share.status === 'active' && (
                      <div className="button-row">
                        <button type="button" className="btn btn-secondary" disabled={guardianBusy} onClick={() => void resendGuardianShare(share.id)}>{t('Resend invite')}</button>
                        <button type="button" className="btn btn-secondary" disabled={guardianBusy} onClick={() => void revokeGuardianShare(share.id)}>{t('Revoke')}</button>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      </div>

      <div className="stack">
        <h3>{t('Shared Learner Summaries')}</h3>
        {guardianLearners.length === 0
          ? <p className="text-muted">{t('Learners who share progress with you will appear here.')}</p>
          : (
            <div className="card-grid" data-testid="learning-guardian-learners">
              {guardianLearners.slice(0, 6).map((learner) => (
                <div key={learner.id} className="card stack">
                  <div className="button-row" style={{ justifyContent: 'space-between' }}>
                    <strong>{learner.learnerName}</strong>
                    <span className="status-pill status-pill--approved">{learner.relationshipLabel || t('Guardian')}</span>
                  </div>
                  <p className="text-muted">{t('Shared with {email}', { email: learner.guardianEmail })}</p>
                  <p className="helper">{t(invitationLabel(learner.invitationStatus || learner.status))}</p>
                  <div className="button-row">
                    {learner.invitationStatus !== 'accepted' && (
                      <button type="button" className="btn btn-secondary" disabled={guardianBusy} onClick={() => void acceptGuardianShare(learner.learnerUserId)}>{t('Accept share')}</button>
                    )}
                    <button type="button" className="btn btn-secondary" disabled={guardianBusy} onClick={() => void openGuardianSummary(learner.learnerUserId)}>{t('View summary')}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        {guardianSummary && (
          <div className="card stack" data-testid="learning-guardian-summary">
            <div className="button-row" style={{ justifyContent: 'space-between' }}>
              <strong>{guardianSummary.progressSummary.learnerName || guardianSummary.share.learnerName}</strong>
              <span className="helper">{guardianSummary.progressSummary.generatedAt ? formatDate(guardianSummary.progressSummary.generatedAt, { month: 'short', day: 'numeric' }) : t('Current')}</span>
            </div>
            <p className="text-muted">{guardianSummary.progressSummary.parentFriendlySummary || t('Progress summary is available.')}</p>
            <div className="button-row">
              <span className="status-pill status-pill--draft">{guardianSummary.progressSummary.currentFocus || t('Current focus')}</span>
              <span className="status-pill status-pill--muted">{t('{count} activities', { count: Number(guardianSummary.progressSummary.metrics?.activitiesCompleted || 0) })}</span>
              <span className="status-pill status-pill--muted">{t('{count} evidence records', { count: Number(guardianSummary.evidenceSummary.total || 0) })}</span>
            </div>
            {guardianSummary.progressSummary.recommendedPracticePlan?.length ? (
              <ul>
                {guardianSummary.progressSummary.recommendedPracticePlan.slice(0, 3).map((item) => <li key={item}>{item}</li>)}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      <AiCoachPanel refreshRevision={coachRevision} />

      <FrenchPractice onPracticeRecorded={refreshAfterPractice} />

      <div className="stack">
        <h3>{t('Continue Learning')}</h3>
        {continueLearning.length === 0
          ? <p className="text-muted">{t('Start a track below to begin your learning path.')}</p>
          : (
            <div className="card-grid" data-testid="learning-continue">
              {continueLearning.map((skill) => (
                <div key={skill.id} className="card stack">
                  <strong>{skill.skillName || skill.skillTrack}</strong>
                  <span className="text-muted">{t(skill.currentLevel || 'beginner')} · {skill.xp} XP</span>
                  {skill.masteryPercent != null && <span className="helper">{t('Mastery')} {Math.round(skill.masteryPercent)}%</span>}
                </div>
              ))}
            </div>
          )}
      </div>

      {recommendedNextSkill && (
        <div className="stack">
          <h3>{t('Recommended Next Skill')}</h3>
          <RecommendationCard recommendation={recommendedNextSkill} />
        </div>
      )}

      {dailyPractice && (
        <div className="stack">
          <h3>{t('Daily Practice')}</h3>
          <div className="card stack" data-testid="learning-daily-practice">
            <strong>{(dailyPractice as any).skillName || (dailyPractice as any).skillTrack || t('Practice session')}</strong>
            <p className="text-muted">{(dailyPractice as any).suggestedNextAction || t('Spend a few minutes practicing today.')}</p>
            {(dailyPractice as any).estimatedEffort && <span className="helper">{(dailyPractice as any).estimatedEffort}</span>}
          </div>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="stack">
          <h3>{t('Recommendations')}</h3>
          <div className="card-grid">
            {recommendations.slice(0, 6).map((recommendation) => (
              <RecommendationCard key={recommendation.id} recommendation={recommendation} />
            ))}
          </div>
        </div>
      )}

      <div className="stack">
        <h3>{t('Recent Activity')}</h3>
        {recentActivities.length === 0
          ? <p className="text-muted">{t('No saved learning activity yet.')}</p>
          : (
            <div className="card-grid" data-testid="learning-recent-activities">
              {recentActivities.slice(0, 4).map((activity) => (
                <div key={activity.id} className="card stack">
                  <div className="button-row" style={{ justifyContent: 'space-between' }}>
                    <strong>{activity.title || activity.skillName || activity.skillTrack || t('Learning activity')}</strong>
                    <span className="helper">{formatDate(activity.completedAt, { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <span className="text-muted">{t(activity.activityType.replace(/_/g, ' '))}</span>
                  <div className="button-row">
                    {scoreLabel(activity.score, activity.maxScore) && <span className="status-pill status-pill--draft">{scoreLabel(activity.score, activity.maxScore)}</span>}
                    {activity.masteryPercent != null && <span className="status-pill status-pill--muted">{Math.round(activity.masteryPercent)}% {t('mastery')}</span>}
                    <span className="status-pill status-pill--muted">{activity.xpEarned} XP</span>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="stack">
        <h3>{t('Coaching History')}</h3>
        {coaching.length === 0
          ? <p className="text-muted">{t('No completed coaching sessions yet.')}</p>
          : (
            <div className="card-grid" data-testid="learning-coaching-history">
              {coaching.slice(0, 4).map((session) => (
                <div key={session.id} className="card stack">
                  <div className="button-row" style={{ justifyContent: 'space-between' }}>
                    <strong>{session.skillName || session.skillTrack || t('Coaching session')}</strong>
                    <span className="helper">{formatDate(session.createdAt, { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <span className="text-muted">{session.coachName || t('Coach')} · {t('{count} minutes', { count: session.durationMinutes })}</span>
                  {session.learnerScoreBefore != null && session.learnerScoreAfter != null && (
                    <span className="status-pill status-pill--draft">{t('Improved by {value}', { value: session.learnerScoreAfter - session.learnerScoreBefore })}</span>
                  )}
                  {session.aiSummary && <p className="helper">{session.aiSummary}</p>}
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="stack">
        <h3>{t('Evidence Ledger')}</h3>
        {evidence.length === 0
          ? <p className="text-muted">{t('Evidence will appear here as lessons, assessments, coaching, and reviews are recorded.')}</p>
          : (
            <div className="card-grid" data-testid="learning-evidence-ledger">
              {evidence.slice(0, 4).map((item) => (
                <div key={item.id} className="card stack">
                  <div className="button-row" style={{ justifyContent: 'space-between' }}>
                    <strong>{item.skillName || item.skillTrack || t('Learning evidence')}</strong>
                    <span className="helper">{formatDate(item.occurredAt, { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <span className="text-muted">{t(item.evidenceType.replace(/_/g, ' '))} · {t(item.source)}</span>
                  <div className="button-row">
                    <span className={item.evidenceStatus === 'verified' ? 'status-pill status-pill--approved' : 'status-pill status-pill--muted'}>{t(item.evidenceStatus)}</span>
                    <span className="status-pill status-pill--muted">{t('Weight')} {item.evidenceWeight}</span>
                    {item.scoreDelta !== 0 && <span className="status-pill status-pill--draft">{t('Score')} {item.scoreDelta > 0 ? '+' : ''}{item.scoreDelta}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="stack">
        <h3>{t('Learning Tracks')}</h3>
        <div className="card-grid" data-testid="learning-starter-tracks">
          {starterTracks.map((track) => (
            <div key={track.id} className="card stack">
              <strong>{track.name}</strong>
              {track.description && <p className="text-muted">{track.description}</p>}
              <span className="helper">{track.supportedLanguages.join(', ')}</span>
            </div>
          ))}
        </div>
      </div>

      {upcomingCoaching.length > 0 && (
        <div className="stack">
          <h3>{t('Upcoming Coaching')}</h3>
          <div className="card-grid">
            {upcomingCoaching.map((assignment) => (
              <div key={assignment.id} className="card stack">
                <strong>{assignment.title || assignment.skillName || t('Coaching session')}</strong>
                {assignment.organizationName && <span className="text-muted">{t('Sponsored by {name}', { name: assignment.organizationName })}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

export const LearningWorkspace:React.FC=()=> {
  const [coachRevision,setCoachRevision]=useState(0)
  return <><JobSkillFocus onGoalSaved={()=>setCoachRevision(value=>value+1)}/><LearningWorkspaceContent coachRevision={coachRevision}/></>
}
